import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, LogOut, Package, ShoppingBag, Store, Wallet } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { VENDOR_STATUS_LABELS, VENDOR_STATUS_STYLES } from "@/lib/format";

const LINKS = [
  { to: "/vendeur", label: "Tableau de bord", icon: BarChart3, end: true },
  { to: "/vendeur/produits", label: "Mes produits", icon: Package },
  { to: "/vendeur/commandes", label: "Mes commandes", icon: ShoppingBag },
  { to: "/vendeur/finances", label: "Solde & commissions", icon: Wallet },
];

export default function VendorLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(undefined);

  useEffect(() => {
    api
      .get("/vendor/me")
      .then((r) => setVendor(r.data.vendor))
      .catch((e) => {
        toast.error(apiError(e));
        setVendor(null);
      });
  }, []);

  if (vendor === undefined) return <p className="p-6 text-sm text-slate-400">Chargement de votre espace vendeur…</p>;

  if (!vendor || vendor.status !== "approved")
    return (
      <div className="mx-auto max-w-lg px-4 py-20 text-center" data-testid="vendor-not-approved">
        <Store className="mx-auto h-12 w-12 text-slate-300" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Espace vendeur indisponible</h1>
        {vendor ? (
          <>
            <span className={`ds-chip mt-3 ${VENDOR_STATUS_STYLES[vendor.status]}`}>
              {VENDOR_STATUS_LABELS[vendor.status]}
            </span>
            <p className="mt-3 text-sm text-slate-600">
              {vendor.status === "pending"
                ? "Votre demande est en cours de validation par l'administrateur."
                : vendor.status_reason || "Votre compte vendeur n'est pas actif. Contactez EMPIRE-SHOP."}
            </p>
          </>
        ) : (
          <p className="mt-2 text-sm text-slate-600">
            Vous n'avez pas encore de profil vendeur.{" "}
            <button type="button" className="font-semibold text-brand" onClick={() => navigate("/devenir-vendeur")}>
              Faire une demande
            </button>
          </p>
        )}
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1300px] items-center gap-3 px-4 py-3">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-gold font-display text-base font-extrabold text-white">
            V
          </span>
          <div>
            <p className="font-display text-sm font-bold leading-none">{vendor.shop_name}</p>
            <p className="text-[10px] text-slate-400">Espace vendeur partenaire · EMPIRE-SHOP</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell dark />
            <button type="button" onClick={() => navigate("/")} className="rounded-xl border border-slate-700 px-3 py-2 text-xs font-semibold hover:bg-slate-800" data-testid="vendor-goto-shop">
              <Store className="mr-1 inline h-3.5 w-3.5" /> Boutique
            </button>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/connexion");
              }}
              className="rounded-xl border border-rose-900 px-3 py-2 text-xs font-semibold text-rose-300 hover:bg-rose-950"
              data-testid="vendor-logout"
            >
              <LogOut className="mr-1 inline h-3.5 w-3.5" /> Quitter
            </button>
          </div>
        </div>
        <nav className="no-scrollbar mx-auto flex max-w-[1300px] gap-1 overflow-x-auto px-4 pb-2">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                  isActive ? "bg-gold text-white" : "text-slate-300 hover:bg-slate-800"
                }`
              }
              data-testid={`vendor-nav-${l.to.split("/").pop()}`}
            >
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-[1300px] px-4 py-5">
        <Outlet context={{ vendor }} />
      </main>
    </div>
  );
}
